import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  MenuItem,
  Select,
  Stack,
  Divider,
  Avatar,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import GroupWorkOutlinedIcon from '@mui/icons-material/GroupWorkOutlined';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

/**
 * Teams — named groups inside the organisation, each with its own list of
 * notification email addresses. Rendered as a card on the Settings page,
 * below Members.
 *
 * Two lists per team, deliberately independent:
 *   • Members — org users assigned to the team. Assignment is about ownership
 *     and in-app targeting; it does NOT subscribe anyone to email.
 *   • Notification emails — standalone addresses (a shared ops inbox, an
 *     external asset manager). These are who actually gets mail.
 *
 * Every mutation endpoint returns the full updated team, so we splice it into
 * local state rather than refetching the whole list — keeps the accordion from
 * collapsing under the user mid-edit.
 */

const displayName = (m) =>
  m.first_name && m.last_name ? `${m.first_name} ${m.last_name}` : (m.email || 'Unknown');

const initialsOf = (m) =>
  ((m.first_name?.[0] || '') + (m.last_name?.[0] || '')).toUpperCase() ||
  (m.email?.[0] || '?').toUpperCase();

const errText = (e, fallback) =>
  e?.response?.data?.message || e?.response?.data?.error || fallback;

const TeamsSection = ({ organisation, isAdmin }) => {
  const [teams, setTeams]     = useState(null);   // null while loading
  const [loadError, setLoadError] = useState('');
  const [error, setError]     = useState('');     // transient mutation errors
  const [busy, setBusy]       = useState('');     // `${teamId}:${action}` key

  const [dialog, setDialog]   = useState(null);   // { mode:'create'|'edit', team? }
  const [confirmDelete, setConfirmDelete] = useState(null);

  const orgMembers = useMemo(() => organisation?.members || [], [organisation]);

  const fetchTeams = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE_URL}/api/teams`);
      setTeams(r.data.teams || []);
      setLoadError('');
    } catch (e) {
      setLoadError(errText(e, 'Failed to load teams'));
      setTeams([]);
    }
  }, []);

  useEffect(() => { fetchTeams(); }, [fetchTeams]);

  // Replace one team in place, so an open accordion stays open.
  const patchTeam = (team) =>
    setTeams((prev) => (prev || []).map((t) => (t.id === team.id ? team : t)));

  const run = async (key, fn, fallbackMsg) => {
    setBusy(key);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(errText(e, fallbackMsg));
    } finally {
      setBusy('');
    }
  };

  const saveTeam = async ({ name, description }) => {
    if (dialog?.mode === 'edit') {
      const r = await axios.patch(`${API_BASE_URL}/api/teams/${dialog.team.id}`, { name, description });
      patchTeam(r.data.team);
    } else {
      const r = await axios.post(`${API_BASE_URL}/api/teams`, { name, description });
      setTeams((prev) => [...(prev || []), r.data.team].sort(
        (a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
      ));
    }
    setDialog(null);
  };

  const deleteTeam = (teamId) =>
    run(`${teamId}:delete`, async () => {
      await axios.delete(`${API_BASE_URL}/api/teams/${teamId}`);
      setTeams((prev) => (prev || []).filter((t) => t.id !== teamId));
      setConfirmDelete(null);
    }, 'Could not delete team');

  const addMember = (teamId, userId) =>
    run(`${teamId}:member`, async () => {
      const r = await axios.post(`${API_BASE_URL}/api/teams/${teamId}/members`, { user_id: userId });
      patchTeam(r.data.team);
    }, 'Could not add member');

  const removeMember = (teamId, userId) =>
    run(`${teamId}:member:${userId}`, async () => {
      const r = await axios.delete(`${API_BASE_URL}/api/teams/${teamId}/members/${userId}`);
      patchTeam(r.data.team);
    }, 'Could not remove member');

  const addEmail = (teamId, email, label) =>
    run(`${teamId}:email`, async () => {
      const r = await axios.post(`${API_BASE_URL}/api/teams/${teamId}/emails`, { email, label });
      patchTeam(r.data.team);
    }, 'Could not add address');

  const toggleEmail = (teamId, emailId, isActive) =>
    run(`${teamId}:email:${emailId}`, async () => {
      const r = await axios.patch(
        `${API_BASE_URL}/api/teams/${teamId}/emails/${emailId}`,
        { is_active: isActive },
      );
      patchTeam(r.data.team);
    }, 'Could not update address');

  const removeEmail = (teamId, emailId) =>
    run(`${teamId}:email:${emailId}`, async () => {
      const r = await axios.delete(`${API_BASE_URL}/api/teams/${teamId}/emails/${emailId}`);
      patchTeam(r.data.team);
    }, 'Could not remove address');

  const saveSchedule = (teamId, patch) =>
    run(`${teamId}:schedule`, async () => {
      const r = await axios.patch(`${API_BASE_URL}/api/teams/${teamId}`, patch);
      patchTeam(r.data.team);
    }, 'Could not save schedule');

  // Send this team's digest immediately, ignoring its schedule, so someone can
  // see the real email without waiting for the chosen day.
  const previewDigest = (teamId) =>
    run(`${teamId}:preview`, async () => {
      const r = await axios.post(
        `${API_BASE_URL}/api/notifications/teams/${teamId}/preview-digest`, {},
      );
      const res = (r.data.results || [])[0];
      if (!res) {
        setError('Nothing sent — check the team has an active address.');
      } else if (res.status === 'sent') {
        setError('');
      } else {
        // 'skipped' is the common one: NOTIFICATIONS_ENABLED is off. Surface the
        // reason verbatim rather than a generic failure.
        setError(`Preview ${res.status}${res.error ? `: ${res.error}` : ''}`);
      }
    }, 'Could not send preview');

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>Teams</Typography>
        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
          {teams ? `${teams.length} ${teams.length === 1 ? 'team' : 'teams'}` : '—'}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 2 }}>
        Assign members to a team, then give the team the addresses that should
        receive its notifications. Addresses are set per team — adding someone
        to a team does not email them.
      </Typography>

      {isAdmin && (
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => setDialog({ mode: 'create' })}
          sx={{ textTransform: 'none', mb: 2 }}
        >
          New team
        </Button>
      )}

      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}

      <Divider sx={{ mb: 1 }} />

      {teams === null && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={22} sx={{ color: 'primary.main' }} />
        </Box>
      )}

      {teams?.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <GroupWorkOutlinedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography sx={{ fontSize: '0.88rem', color: 'text.secondary' }}>
            No teams yet.
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: 'text.disabled' }}>
            {isAdmin
              ? 'Create one to start routing notifications.'
              : 'Only admins can create teams.'}
          </Typography>
        </Box>
      )}

      {teams?.map((team) => (
        <TeamRow
          key={team.id}
          team={team}
          isAdmin={isAdmin}
          orgMembers={orgMembers}
          busy={busy}
          onEdit={() => setDialog({ mode: 'edit', team })}
          onDelete={() => setConfirmDelete(team)}
          onAddMember={addMember}
          onRemoveMember={removeMember}
          onAddEmail={addEmail}
          onToggleEmail={toggleEmail}
          onRemoveEmail={removeEmail}
          onSaveSchedule={saveSchedule}
          onPreview={previewDigest}
        />
      ))}

      {dialog && (
        <TeamDialog
          mode={dialog.mode}
          team={dialog.team}
          onSave={saveTeam}
          onClose={() => setDialog(null)}
        />
      )}

      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete team</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.88rem' }}>
            Delete <strong>{confirmDelete?.name}</strong>? Its members are unassigned
            and its {confirmDelete?.emails?.length || 0} notification{' '}
            {confirmDelete?.emails?.length === 1 ? 'address' : 'addresses'} are removed.
            Nobody loses their Lumeros account.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteTeam(confirmDelete.id)}
            disabled={busy === `${confirmDelete?.id}:delete`}
            sx={{ textTransform: 'none' }}
          >
            {busy === `${confirmDelete?.id}:delete` ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

/* ─── One team, expandable ──────────────────────────────────────────────── */

const TeamRow = ({
  team, isAdmin, orgMembers, busy,
  onEdit, onDelete, onAddMember, onRemoveMember,
  onAddEmail, onToggleEmail, onRemoveEmail,
  onSaveSchedule, onPreview,
}) => {
  const [pickUser, setPickUser] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const memberIds = new Set((team.members || []).map((m) => m.id));
  const assignable = orgMembers.filter((m) => !memberIds.has(m.id));
  const activeEmails = (team.emails || []).filter((e) => e.is_active).length;

  const submitEmail = async (e) => {
    e?.preventDefault();
    if (!newEmail.trim()) return;
    await onAddEmail(team.id, newEmail.trim().toLowerCase(), newLabel.trim());
    setNewEmail('');
    setNewLabel('');
  };

  const submitMember = async () => {
    if (!pickUser) return;
    await onAddMember(team.id, pickUser);
    setPickUser('');
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        bgcolor: 'transparent',
        '&:before': { display: 'none' },
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, pr: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>{team.name}</Typography>
            {team.description && (
              <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>
                {team.description}
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1 }} />
          <Chip
            label={`${team.members?.length || 0} ${team.members?.length === 1 ? 'member' : 'members'}`}
            size="small"
            sx={{ height: 20, fontSize: '0.68rem' }}
          />
          <Chip
            icon={<MailOutlineIcon sx={{ fontSize: 13 }} />}
            label={activeEmails}
            size="small"
            sx={{
              height: 20, fontSize: '0.68rem',
              bgcolor: activeEmails ? 'rgba(217, 138, 43, 0.15)' : undefined,
              color: activeEmails ? 'primary.main' : 'text.secondary',
            }}
          />
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 0.5, pt: 0, pb: 2.5 }}>
        {/* ── Members ─────────────────────────────────────────────── */}
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em',
                          color: 'text.secondary', mb: 1 }}>
          MEMBERS
        </Typography>

        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
          {(team.members || []).length === 0 && (
            <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>
              Nobody assigned.
            </Typography>
          )}
          {(team.members || []).map((m) => (
            <Chip
              key={m.id}
              avatar={
                <Avatar sx={{ bgcolor: 'grey.700', color: '#F4F2EC',
                              fontSize: '0.62rem !important', fontWeight: 700 }}>
                  {initialsOf(m)}
                </Avatar>
              }
              label={displayName(m)}
              size="small"
              onDelete={isAdmin ? () => onRemoveMember(team.id, m.id) : undefined}
              disabled={busy === `${team.id}:member:${m.id}`}
              sx={{ fontSize: '0.75rem' }}
            />
          ))}
        </Stack>

        {isAdmin && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2.5 }}>
            <Select
              value={pickUser}
              onChange={(e) => setPickUser(e.target.value)}
              displayEmpty
              size="small"
              disabled={assignable.length === 0}
              sx={{ minWidth: 240, fontSize: '0.82rem' }}
              renderValue={(v) =>
                v
                  ? displayName(assignable.find((m) => m.id === v) || {})
                  : (assignable.length ? 'Add a member…' : 'Everyone is on this team')
              }
            >
              {assignable.map((m) => (
                <MenuItem key={m.id} value={m.id} sx={{ fontSize: '0.82rem' }}>
                  {displayName(m)}
                  <Typography component="span" sx={{ fontSize: '0.72rem', color: 'text.secondary', ml: 1 }}>
                    {m.email}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
            <Button
              variant="outlined"
              size="small"
              onClick={submitMember}
              disabled={!pickUser || busy === `${team.id}:member`}
              startIcon={busy === `${team.id}:member`
                ? <CircularProgress size={13} />
                : <PersonAddAltIcon sx={{ fontSize: 15 }} />}
              sx={{ textTransform: 'none' }}
            >
              Assign
            </Button>
          </Box>
        )}

        {/* ── Notification emails ─────────────────────────────────── */}
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em',
                          color: 'text.secondary', mb: 0.5 }}>
          NOTIFICATION EMAILS
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', mb: 1.25 }}>
          Where this team's alerts and reports are sent. Mute an address to stop
          delivery without removing it.
        </Typography>

        <Stack spacing={0.5} sx={{ mb: 1.5 }}>
          {(team.emails || []).length === 0 && (
            <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>
              No addresses yet — this team will not receive notifications.
            </Typography>
          )}
          {(team.emails || []).map((em) => {
            const emBusy = busy === `${team.id}:email:${em.id}`;
            return (
              <Box
                key={em.id}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  py: 0.6, px: 1, borderRadius: 1,
                  bgcolor: 'action.hover',
                  opacity: em.is_active ? 1 : 0.55,
                }}
              >
                <MailOutlineIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                <Typography sx={{
                  fontSize: '0.8rem', fontFamily: 'monospace',
                  textDecoration: em.is_active ? 'none' : 'line-through',
                }}>
                  {em.email}
                </Typography>
                {em.label && (
                  <Chip label={em.label} size="small" sx={{ height: 18, fontSize: '0.66rem' }} />
                )}
                {!em.is_active && (
                  <Chip label="Muted" size="small"
                        sx={{ height: 18, fontSize: '0.66rem', color: 'text.secondary' }} />
                )}
                <Box sx={{ flex: 1 }} />
                {isAdmin && (
                  <>
                    <Tooltip title={em.is_active ? 'Mute this address' : 'Unmute this address'}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={emBusy}
                          onClick={() => onToggleEmail(team.id, em.id, !em.is_active)}
                          sx={{ color: 'text.secondary' }}
                        >
                          {emBusy ? <CircularProgress size={14} />
                            : em.is_active
                              ? <NotificationsActiveOutlinedIcon sx={{ fontSize: 16 }} />
                              : <NotificationsOffOutlinedIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Remove address">
                      <span>
                        <IconButton
                          size="small"
                          disabled={emBusy}
                          onClick={() => onRemoveEmail(team.id, em.id)}
                          sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}
              </Box>
            );
          })}
        </Stack>

        {isAdmin && (
          <Box component="form" onSubmit={submitEmail}
               sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              size="small"
              placeholder="ops@company.com"
              sx={{ flex: 1, maxWidth: 280 }}
            />
            <TextField
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              size="small"
              placeholder="Label (optional)"
              sx={{ width: 170 }}
            />
            <Button
              type="submit"
              variant="outlined"
              size="small"
              disabled={!newEmail.trim() || busy === `${team.id}:email`}
              startIcon={busy === `${team.id}:email`
                ? <CircularProgress size={13} />
                : <AddIcon sx={{ fontSize: 15 }} />}
              sx={{ textTransform: 'none' }}
            >
              Add
            </Button>
          </Box>
        )}

        {/* ── Weekly notification schedule ─────────────────────────── */}
        <TeamSchedule
          team={team}
          isAdmin={isAdmin}
          busy={busy === `${team.id}:schedule`}
          previewing={busy === `${team.id}:preview`}
          onSave={(patch) => onSaveSchedule(team.id, patch)}
          onPreview={() => onPreview(team.id)}
        />

        {isAdmin && (
          <Box sx={{ display: 'flex', gap: 0.5, mt: 2.5 }}>
            <Button size="small" onClick={onEdit}
                    startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                    sx={{ textTransform: 'none', color: 'text.secondary' }}>
              Rename
            </Button>
            <Button size="small" onClick={onDelete}
                    startIcon={<DeleteOutlineIcon sx={{ fontSize: 15 }} />}
                    sx={{ textTransform: 'none', color: 'text.secondary',
                          '&:hover': { color: 'error.main' } }}>
              Delete team
            </Button>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

/* ─── Weekly notification schedule ──────────────────────────────────────── */

const DAYS = [
  { v: 0, label: 'Monday' }, { v: 1, label: 'Tuesday' }, { v: 2, label: 'Wednesday' },
  { v: 3, label: 'Thursday' }, { v: 4, label: 'Friday' }, { v: 5, label: 'Saturday' },
  { v: 6, label: 'Sunday' },
];

// Curated rather than the full IANA list (~600 entries): these cover the NEM
// plus the offshore investors who read these reports. The stored value is
// always included below even if it isn't here, so nothing is ever unselectable.
const TIMEZONES = [
  'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane',
  'Australia/Adelaide', 'Australia/Perth', 'Australia/Darwin',
  'Australia/Hobart', 'Pacific/Auckland', 'Asia/Singapore', 'Asia/Tokyo',
  'Europe/London', 'Europe/Paris', 'America/New_York', 'UTC',
];

const TeamSchedule = ({ team, isAdmin, busy, previewing, onSave, onPreview }) => {
  const enabled  = Boolean(team.notify_enabled);
  const day      = team.notify_day_of_week;
  const time     = team.notify_time || '09:00';
  const tz       = team.notify_timezone || 'Australia/Sydney';
  const horizon  = team.notify_horizon_days || 7;

  // Local draft so typing in the time field doesn't fire a request per keystroke.
  // `touched` matters: the draft defaults to 09:00 while the stored value is
  // null, so a plain compare on blur would silently persist 09:00 the first
  // time anyone expands a team and clicks away. Only an actual edit commits.
  const [draftTime, setDraftTime] = useState(time);
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    setDraftTime(team.notify_time || '09:00');
    setTouched(false);
  }, [team.notify_time]);

  const tzOptions = TIMEZONES.includes(tz) ? TIMEZONES : [tz, ...TIMEZONES];
  const ready = day !== null && day !== undefined && Boolean(team.notify_time);

  const summary = ready
    ? `Every ${DAYS.find((d) => d.v === day)?.label} at ${time} (${tz})`
    : 'Pick a day and time to enable';

  return (
    <Box sx={{ mt: 2.5 }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em',
                        color: 'text.secondary', mb: 0.5 }}>
        WEEKLY SCHEDULE
      </Typography>
      <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', mb: 1.25 }}>
        Emails this team its tasks for the next {horizon} days. Sent once on the
        chosen day at the chosen local time.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Select
          value={day ?? ''}
          onChange={(e) => onSave({ notify_day_of_week: e.target.value === '' ? null : Number(e.target.value) })}
          size="small"
          displayEmpty
          disabled={!isAdmin || busy}
          sx={{ minWidth: 130, height: 32, fontSize: '0.78rem' }}
          renderValue={(v) => (v === '' || v === null || v === undefined
            ? <Box component="span" sx={{ color: 'text.disabled' }}>Day…</Box>
            : DAYS.find((d) => d.v === v)?.label)}
        >
          {DAYS.map((d) => (
            <MenuItem key={d.v} value={d.v} sx={{ fontSize: '0.78rem' }}>{d.label}</MenuItem>
          ))}
        </Select>

        <TextField
          type="time"
          value={draftTime}
          onChange={(e) => { setDraftTime(e.target.value); setTouched(true); }}
          // Commit on blur / Enter rather than per keystroke — a native time
          // input emits a change for every digit.
          onBlur={() => {
            if (touched && draftTime && draftTime !== team.notify_time) {
              setTouched(false);
              onSave({ notify_time: draftTime });
            }
          }}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          size="small"
          disabled={!isAdmin || busy}
          inputProps={{ step: 300 }}
          sx={{ width: 110, '& .MuiOutlinedInput-root': { height: 32, fontSize: '0.78rem' } }}
        />

        <Select
          value={tz}
          onChange={(e) => onSave({ notify_timezone: e.target.value })}
          size="small"
          disabled={!isAdmin || busy}
          sx={{ minWidth: 178, height: 32, fontSize: '0.78rem' }}
        >
          {tzOptions.map((z) => (
            <MenuItem key={z} value={z} sx={{ fontSize: '0.78rem' }}>{z}</MenuItem>
          ))}
        </Select>

        <Select
          value={horizon}
          onChange={(e) => onSave({ notify_horizon_days: Number(e.target.value) })}
          size="small"
          disabled={!isAdmin || busy}
          sx={{ minWidth: 104, height: 32, fontSize: '0.78rem' }}
        >
          {[7, 14, 30].map((n) => (
            <MenuItem key={n} value={n} sx={{ fontSize: '0.78rem' }}>{n} days</MenuItem>
          ))}
        </Select>

        {busy && <CircularProgress size={14} />}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1.25, flexWrap: 'wrap' }}>
        <Chip
          icon={enabled
            ? <NotificationsActiveOutlinedIcon sx={{ fontSize: 13 }} />
            : <NotificationsOffOutlinedIcon sx={{ fontSize: 13 }} />}
          label={enabled ? summary : 'Off'}
          size="small"
          sx={{
            height: 22, fontSize: '0.7rem',
            bgcolor: enabled ? 'rgba(217, 138, 43, 0.15)' : 'transparent',
            color: enabled ? 'primary.main' : 'text.disabled',
            border: enabled ? 'none' : '1px dashed',
            borderColor: 'divider',
          }}
        />
        {isAdmin && (
          <>
            <Tooltip title={ready ? '' : 'Choose a day and time first'}>
              <span>
                <Button
                  size="small"
                  onClick={() => onSave({ notify_enabled: !enabled })}
                  disabled={busy || (!enabled && !ready)}
                  sx={{ textTransform: 'none', fontSize: '0.73rem' }}
                >
                  {enabled ? 'Turn off' : 'Turn on'}
                </Button>
              </span>
            </Tooltip>
            <Button
              size="small"
              onClick={onPreview}
              disabled={previewing}
              startIcon={previewing
                ? <CircularProgress size={12} />
                : <MailOutlineIcon sx={{ fontSize: 14 }} />}
              sx={{ textTransform: 'none', fontSize: '0.73rem', color: 'text.secondary' }}
            >
              Send preview now
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

/* ─── Create / rename dialog ────────────────────────────────────────────── */


const TeamDialog = ({ mode, team, onSave, onClose }) => {
  const [name, setName] = useState(team?.name || '');
  const [description, setDescription] = useState(team?.description || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setErr('');
    try {
      await onSave({ name: name.trim(), description: description.trim() });
    } catch (e2) {
      setErr(errText(e2, 'Could not save team'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <Box component="form" onSubmit={submit}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {mode === 'edit' ? 'Rename team' : 'New team'}
        </DialogTitle>
        <DialogContent>
          {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
          <TextField
            autoFocus
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            fullWidth
            sx={{ mb: 2, mt: 1 }}
            placeholder="Operations"
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
            placeholder="What this team is notified about"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || !name.trim()}
            sx={{ textTransform: 'none' }}
          >
            {saving ? <CircularProgress size={18} /> : (mode === 'edit' ? 'Save' : 'Create')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default TeamsSection;
